import _ from 'lodash'
import Cookies from 'js-cookie'
import queryString from 'query-string'
import Config from '../configs'
import fetcher from '../lib/fetcher'
import I18n from '../lib/i18n'
import 'whatwg-fetch'

const intercept401 = (req, res) => {
  const req_slug_extract = req.match(/by_slug_name\/(.*)\?/)
  const req_slug = _.isEmpty(req_slug_extract) ? null : req_slug_extract[1]
  // do not kick user if fetching a previous databin return 401, only remove it from localStorage
  if (!_.isEmpty(req_slug) && req_slug !== getSlugNameFromURL()) return
  if (res.status === 401) window.dispatchEvent(new Event('kick_user'))
}

const OM_API = new fetcher({
  baseUrl: Config.get('API_URL'),
  interceptor: intercept401
})
const CHANNEL = Config.get('API_CHANNEL')
const Rollbar = window.Rollbar // This will have something if Rollbar is integrated

let storage = {}

export function getSlugNameFromURL() {
  const params = queryString.parse(window.location.search)
  // split by ? is required here as sometimes there are search engines append queries
  // like this: `slug=IkxAuw?nsukey=ObvScKc9X`)
  return (params.slug||'').split('?')[0]
}

export function getCookie(cookieName) {
  let cookie_data

  try {
    cookie_data = JSON.parse(Cookies.get(cookieName))
  } catch(err) {}

  return cookie_data
}

export function setCookie(cookieName, value) {
  // expire in 2 hours
  Cookies.set(cookieName, JSON.stringify(value))
}

export function removeCookie(cookieName) {
  Cookies.remove(cookieName)
}

export function getStorage(property_name) {
  // get data from localStorage
  let data
  try {
    data = localStorage.getItem(property_name) || _.get(storage, property_name)

    if (!_.isEmpty(data)) {
      return JSON.parse(data)
    }
  } catch (ex) {
      console.log(`incorrect json format on data from ${property_name} in storage`)
  }
}

export function setStorage(property_name, data) {
  try {
    localStorage.setItem(property_name, JSON.stringify(data))
  } catch (ex) {
    storage[property_name] = JSON.stringify(data)
  }

}

export function fetchSystemSetting() {
  const requestURL = `/api/v2/${I18n.currentLocale()}/cdb/system_settings`

  return OM_API.get(requestURL)
}

export async function updateSlugAndMergeDatabin() {
  const current_slug = getSlugNameFromURL()
  const local_storage_slugs = getStorage('slug') || []
  const setSlug = () => setStorage('slug', [getSlugNameFromURL()])
  let slugs_data = {}
  let new_databin_data = {}
  // skip if the last of local storage's slugs is current slug
  if (_.last(local_storage_slugs) === current_slug) return Promise.resolve({})
  // merge last datbin items into new databin

  try {
    // fetch for databin data for slugs store in local storage
    const local_storage_databins_data_fetches = _.map(local_storage_slugs, async (slug) => {
      const databin_data = await fetchDatabin(slug)
      const shopping_cart_items = _.get(databin_data, 'data.data_bin.data.shopping_cart_items', [])

      slugs_data[slug] = { shopping_cart_items, status: 'expired' }
      new_databin_data.merged_from = slug

      return Promise.resolve(shopping_cart_items)
    })
    const local_storage_databins_shopping_cart_items = await Promise.all(local_storage_databins_data_fetches)
    // fetch current slug's data
    const current_databin_data = await fetchDatabin()
    let current_databins_shopping_cart_items = _.get(current_databin_data, 'data.data_bin.data.shopping_cart_items', [])

    // update the current databin with local storage slug's data
    local_storage_databins_shopping_cart_items.forEach(shopping_cart_items => {
      const mingled_shopping_cart_items = mingledShoppingCartItems(shopping_cart_items, current_databins_shopping_cart_items)
      new_databin_data.shopping_cart_items = mingled_shopping_cart_items
    })

    local_storage_databins_shopping_cart_items.length > 0 && await updateDatabin(new_databin_data)
    // expired merged databin
    local_storage_slugs.forEach(slug => updateDatabin(slugs_data[slug], slug))
    // clear the storage with the current slug
    setSlug()

    return Promise.resolve({})
  } catch(err) {
    // ignore error when the databin is not found
    if (err.code === 401) {
      setSlug()
      return Promise.resolve([])
    } else {
      return Promise.reject(err)
    }
  }
}

function mingledShoppingCartItems(shopping_cart_items, all_shopping_carts_items = []) {
  let result = _.cloneDeep(all_shopping_carts_items)
  shopping_cart_items.forEach((shopping_cart_item) => {
    const same_shopping_cart_item = _.find(result, { sku_id: shopping_cart_item.sku_id })

    if (_.isEmpty(same_shopping_cart_item)) {
      result = _.concat(result, shopping_cart_item)
    } else {
      same_shopping_cart_item.quantity = same_shopping_cart_item.quantity + shopping_cart_item.quantity
    }
  })

  return result
}

export function fetchOrderBySlugName(id, slug_name) {
  const params = {
    schema_version: '2019-04-11',
    includes: 'shipments'
  }

  let requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/orders/${id}/with_cdb_slug_name/${slug_name}`
  requestURL += `?${queryString.stringify(params)}`

  return OM_API.get(requestURL)
}

export function fetchDatabin(slug) {
  slug = _.isEmpty(slug) ? getSlugNameFromURL() : slug
  const requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/data_bins/by_slug_name/${slug}?schema_version=2019-03-25`

  return OM_API.get(requestURL, { 'X-CDB-SLUG-NAME': slug })
}

export function updateDatabin(data, slug) {
  slug = _.isEmpty(slug) ? getSlugNameFromURL() : slug
  const requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/data_bins/by_slug_name/${slug}`
  const params = {
    schmea_version: '2019-03-25',
    data: {
      data_bin: {
        data: {
          delivery_type: 'collect_at_store',
          status: 'closed',
          ...data
        }
      }
    }
  }

  return OM_API.put(requestURL, params, { 'X-CDB-SLUG-NAME': slug })
}

export function fetchSkusDetails(ids, page = 1) {
  const include_params = [
    'price_details',
    'product',
    'color_option',
    'size_option',
    'stock_level'
  ].join(',')
  const params = {
    includes: include_params,
    // TODO change to id instead of sku_ids
    // id: ids,
    sku_ids: ids,
    schema_version: '2018-04-18',
    page: page,
    page_size: Config.get('FETCH_SKU_PAGE_SIZE')
  }

  let requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/skus`;
  requestURL += `?${queryString.stringify(params, { arrayFormat: 'bracket' })}`

  return OM_API.get(requestURL)
}

export function fetchDepartmentsWithCategory(query_params) {
  const includes = [
    'categories',
    'categories.children'
  ].join(',')
  const params = {
    includes,
    ...query_params,
    sku_active: true,
    schema_version: '2019-05-03'
  }

  let requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/departments`
  requestURL += `?${queryString.stringify(params, { arrayFormat: 'bracket' })}`

  return OM_API.get(requestURL)
}

export function fetchProducts(q, search_by = '', page = 1) {
  const c = [
    'sku_code',
    'product_title',
    'color_name',
    'color_code',
  ].join(',')
  const include_params = [
    'product',
    'products.color_options',
    'products.size_options',
    'color_options.price_details',
    'color_options.stock_level',
    'checkout_settings'
  ].join(',')
  q = _.trim(q)
  const qa = /\W/.test(q) ? 'ilike' : '~*'
  const params = {
    schema_version: '2018-04-18',
    includes: include_params,
    product_type: 'product',
    distinct: 'p',
    with_inventory: true,
    page: page,
    [search_by]: q,
    qa,
    c,
  }

  let requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/skus`;
  requestURL += `?${queryString.stringify(params)}`

  return OM_API.get(requestURL)
}

export function fetchProduct(id) {
  const include_params = [
    'skus',
    'skus.color_option_id',
    'skus.size_option',
    'skus.size_option_id',
    'skus.price_details',
    'skus.stock_level'
  ].join(',')
  const params = {
    schema_version: '2018-04-18',
    includes: include_params,
    with_inventory: true,
    page_size: 50
  }

  let requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/products/${id}`;
  requestURL += `?${queryString.stringify(params)}`

  return OM_API.get(requestURL)
}

export function fetchContentGroup(code) {
  const requestURL = `/api/content_groups/${code}?locale=${I18n.currentLocale()}`

  return OM_API.get(requestURL)
}

export function createPaymentRequest(payment_request) {
  const requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/payment_requests?includes=meta`
  const params = {
    schema_version: '2019-04-10',
    cdb_slug_name: getSlugNameFromURL(),
    data: { payment_request }
  }

  return OM_API.post(requestURL, params)
}

export function createOrder(customer_info, total_price) {
  const requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/orders/cdb_checkout?includes=available_payment_providers,price_details,can_defer_payment`
  const params = {
    schema_version: '2019-04-06',
    data: {
      order: {
        meta: !_.isEmpty(customer_info)
          ? { cdb_customer_profile: customer_info }
          : {}
      },
      shopping_cart_total_price: `${total_price}`
    }
  }

  return OM_API.post(requestURL, params)
}

export function getOpenId(payment_provider_id, redirect_url) {
  const requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/payment_requests/qfpay_overseas_oauths/oauth_url?payment_provider_id=${payment_provider_id}&redirect_url=${redirect_url}`

  return OM_API.get(requestURL)
}

export function fetchOrder(id) {
  const include_params = [
    'available_payment_providers',
    'price_details'
  ]
  const params = {
    schema_version: '2019-04-11',
    includes: include_params
  }

  let requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/orders/${id}/with_cdb_slug_name/${getSlugNameFromURL()}`
  requestURL += `?${queryString.stringify(params)}`

  return OM_API.get(requestURL)
}

export function saveCompletedOrderToStorage(order_id) {
  const completed_orders = getStorage('completed_orders')
  const new_completed_orders = _.isEmpty(completed_orders)
    ? [{ order_id, slug_name: getSlugNameFromURL() }]
    : _.concat(completed_orders, { order_id, slug_name: getSlugNameFromURL() })

  setStorage('completed_orders', new_completed_orders)
}

export function removeCompletedOrderFromStorage(order_id) {
  let new_completed_orders = getStorage('completed_orders')
  _.remove(new_completed_orders, order => order.order_id === order_id)
  setStorage('completed_orders', new_completed_orders)
}

export function handleError(err) {
  let error_message = ''
  let inline_errors = {}

  if (err instanceof TypeError) {
    // TypeError should not show to user, simply return a generic message and log the issue.
    error_message = I18n.t('errors.500')
    Rollbar && Rollbar.error('JS TypeError', err)
  } else if (_.isArray(err.errors)) {
    err.errors.forEach((error) => {
      const { message, type, code } = error
      switch (type) {
        case 'general':
          error_message = message
          break
        case 'shopping_cart_item_error':
          const sku_ids = _.get(error, 'data.sku_ids')
          let inline_message

          switch(code) {
            case '30201':
              inline_message = I18n.t('errors.out_of_stock')
              break
            case '30202':
              inline_message = I18n.t('errors.not_allow_to_checkout')
              break
            default:
              inline_message = message
              break
          }

          sku_ids.forEach((sku_id) => {
            inline_errors[sku_id] = {
              message: inline_message,
              code
            }
          })
          break
        default:
          break
      }
    })
  } else if (err.offline) {
    error_message = I18n.t('errors.offline')
  } else {
    error_message = I18n.t('errors.500')
    Rollbar && Rollbar.error('API Error', err)
  }

  return _.pickBy({
    ..._.pick(err, ['status', 'code']),
    error_message,
    inline_errors
  }, _.identity)
}

export function validatePhone(phone) {
  const requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/phones/formatter`
  const params = {
    schema_version: '2019-03-29',
    data: {
      phone: { phone }
    }
  }

  return OM_API.post(requestURL, params)
}

export function validateMerchant(payment_request_id, apple_pay_merchant_data) {
  const requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/payment_requests/${payment_request_id}/apple_pay_merchants/create_session`
  const params = {
    schema_version: "2019-04-16",
    cdb_slug_name: getSlugNameFromURL(),
    data: { apple_pay_merchant: apple_pay_merchant_data }
  }

  return OM_API.post(requestURL, params)
}

export function authorizeApplePay(payment_response) {
  const requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/payment_responses`
  const params = {
    schema_version: "2019-04-16",
    cdb_slug_name: getSlugNameFromURL(),
    data: { payment_response }
  }

  return OM_API.post(requestURL, params)
}

export function markOrderAsDeferPayment(order_id) {
  const requestURL = `/api/v2/${I18n.currentLocale()}/${CHANNEL}/orders/${order_id}/mark_payment_deferred`
  const params = {
    schema_version: '2019-05-03',
    data: {}
  }

  return OM_API.post(requestURL, params)
}
/**
 * Example:
 *
 * export function getOMExample() {
 *   return OM_API.get(`${I18n.currentLocale()}/api/rsvp/example`)
 * }
 */



// WEBPACK FOOTER //
// ./src/api/index.js